import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn, Card, Label, Input, Select } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, Plus, Upload, Pencil, Trash2, FileSpreadsheet, HandCoins } from "lucide-react";

const money = (v) => (v == null ? null : `$${Number(v).toFixed(2)}`);
const blankRow = { category: "General", service: "", unit: "each", price: "" };

export default function Pricing() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("sheets");
  return (
    <div className="space-y-6">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Swift Pricing Engine</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{t("pricing.title")}</h1>
        <p className="text-zinc-500 text-sm mt-1 font-mono">{t("pricing.subtitle")}</p>
      </div>
      <div className="flex gap-2 border-b-2 border-zinc-800">
        {[["sheets", t("pricing.clientSheets"), FileSpreadsheet], ["pay", t("pricing.paySheet"), HandCoins]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`pricing-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 font-head font-bold uppercase text-sm tracking-wide -mb-0.5 border-b-2 transition-colors ${tab === k ? "border-[#FF5722] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === "sheets" ? <ClientSheets /> : <PaySheet />}
    </div>
  );
}

function ClientSheets() {
  const { t } = useTranslation();
  const [sheets, setSheets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newSheet, setNewSheet] = useState({ name: "", notes: "" });
  const [rowEdit, setRowEdit] = useState(null);
  const fileRef = useRef(null);

  const loadSheets = useCallback(() => api.get("/pricing/sheets").then((r) => {
    setSheets(r.data);
    setActiveId((cur) => cur || (r.data[0]?.id ?? null));
  }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  const loadSheet = useCallback((id) => id && api.get(`/pricing/sheets/${id}`).then((r) => setSheet(r.data)).catch(() => {}), []);

  useEffect(() => { loadSheets(); }, [loadSheets]);
  useEffect(() => { loadSheet(activeId); }, [activeId, loadSheet]);

  const createSheet = async () => {
    if (!newSheet.name.trim()) { toast.error("Name required"); return; }
    try { const { data } = await api.post("/pricing/sheets", newSheet); toast.success("Sheet created"); setNewOpen(false); setNewSheet({ name: "", notes: "" }); await loadSheets(); setActiveId(data.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delSheet = async (s) => { if (!window.confirm(`Delete sheet "${s.name}"? Its rows will be removed.`)) return; try { await api.delete(`/pricing/sheets/${s.id}`); toast.success("Deleted"); setActiveId(null); setSheet(null); loadSheets(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const saveRow = async () => {
    if (!rowEdit.service.trim()) { toast.error("Service required"); return; }
    const payload = { category: rowEdit.category, service: rowEdit.service, unit: rowEdit.unit, price: rowEdit.price === "" ? null : Number(rowEdit.price) };
    try {
      if (rowEdit.id) await api.put(`/pricing/items/${rowEdit.id}`, payload);
      else await api.post(`/pricing/sheets/${activeId}/items`, payload);
      toast.success("Saved"); setRowEdit(null); loadSheet(activeId); loadSheets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delRow = async (row) => { if (!window.confirm(`Delete "${row.service}"?`)) return; try { await api.delete(`/pricing/items/${row.id}`); loadSheet(activeId); loadSheets(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/pricing/sheets/${activeId}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.inserted} rows`); loadSheet(activeId); loadSheets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const grouped = (sheet?.items || []).reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {});

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <div className="space-y-2 min-w-0">
        <div className="flex items-center justify-between">
          <div className="font-head font-bold text-white uppercase text-sm tracking-wide">{t("pricing.clientSheets")}</div>
          <Btn data-testid="new-sheet-btn" variant="secondary" onClick={() => setNewOpen(true)}><Plus size={14} /></Btn>
        </div>
        {sheets.map((s) => (
          <button key={s.id} data-testid={`sheet-${s.id}`} onClick={() => setActiveId(s.id)}
            className={`w-full text-left px-3 py-2 border-2 transition-colors ${activeId === s.id ? "border-[#FF5722] bg-[#FF5722]/10" : "border-zinc-800 hover:border-zinc-600"}`}>
            <div className="font-head font-bold text-white text-sm">{s.name}</div>
            <div className="font-mono text-[10px] text-zinc-500">{s.item_count} items</div>
          </button>
        ))}
      </div>

      <div className="space-y-4 min-w-0">
        {sheet ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-head font-black text-white text-xl uppercase tracking-tight">{sheet.name}</div>
                {sheet.notes && <p className="text-zinc-500 text-xs font-mono mt-0.5">{sheet.notes}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.pdf" className="hidden" onChange={onUpload} data-testid="sheet-upload-input" />
                <Btn data-testid="upload-sheet-btn" variant="secondary" onClick={() => fileRef.current?.click()}><Upload size={14} /> {t("pricing.uploadSheet")}</Btn>
                <Btn data-testid="add-row-btn" onClick={() => setRowEdit({ ...blankRow })}><Plus size={14} /> {t("pricing.addRow")}</Btn>
                <Btn data-testid="del-sheet-btn" variant="danger" onClick={() => delSheet(sheets.find((x) => x.id === activeId))}><Trash2 size={14} /></Btn>
              </div>
            </div>
            <p className="font-mono text-[11px] text-zinc-500">{t("pricing.sheetNote")}</p>
            {Object.keys(grouped).length === 0 ? (
              <Card className="p-8 text-center text-zinc-600 font-mono">No rows yet. Add one or upload a sheet.</Card>
            ) : Object.entries(grouped).map(([cat, items]) => (
              <Card key={cat} className="p-0 overflow-hidden">
                <div className="bg-zinc-900 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[#FF5722] border-b border-zinc-800">{cat}</div>
                <div className="divide-y divide-zinc-800">
                  {items.map((it) => (
                    <div key={it.id} data-testid={`row-${it.id}`} className="flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-white truncate">{it.service}</div>
                        <div className="font-mono text-[10px] text-zinc-600">{it.unit}</div>
                      </div>
                      <div className="text-right w-16 sm:w-24 shrink-0"><div className="font-mono text-white text-xs sm:text-sm">{money(it.client_price) ?? t("pricing.byBid")}</div><div className="font-mono text-[9px] text-zinc-600 uppercase hidden sm:block">{t("pricing.clientPrice")}</div></div>
                      <div className="text-right w-16 sm:w-24 shrink-0"><div className="font-mono text-emerald-400 text-xs sm:text-sm">{money(it.contractor_pay) ?? "—"}</div><div className="font-mono text-[9px] text-zinc-600 uppercase hidden sm:block">{t("pricing.contractorPay")}</div></div>
                      <button data-testid={`edit-row-${it.id}`} onClick={() => setRowEdit({ id: it.id, category: it.category, service: it.service, unit: it.unit, price: it.client_price ?? "" })} className="text-zinc-500 hover:text-[#FF5722] shrink-0"><Pencil size={14} /></button>
                      <button data-testid={`del-row-${it.id}`} onClick={() => delRow(it)} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </>
        ) : <Card className="p-10 text-center text-zinc-600 font-mono">Select or create a client price sheet.</Card>}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-sheet-dialog"><DialogHeader><DialogTitle className="font-head uppercase">{t("pricing.newSheet")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Client / Sheet name</Label><Input data-testid="new-sheet-name" value={newSheet.name} onChange={(e) => setNewSheet({ ...newSheet, name: e.target.value })} /></div>
            <div><Label>Notes</Label><Input data-testid="new-sheet-notes" value={newSheet.notes} onChange={(e) => setNewSheet({ ...newSheet, notes: e.target.value })} /></div>
            <p className="font-mono text-[10px] text-zinc-500">{t("pricing.uploadHint")}</p>
            <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setNewOpen(false)}>{t("common.cancel")}</Btn><Btn data-testid="create-sheet-btn" onClick={createSheet}>{t("common.save")}</Btn></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rowEdit} onOpenChange={(o) => !o && setRowEdit(null)}>
        <DialogContent data-testid="row-dialog"><DialogHeader><DialogTitle className="font-head uppercase">{rowEdit?.id ? t("common.edit") : t("pricing.addRow")}</DialogTitle></DialogHeader>
          {rowEdit && (
            <div className="space-y-3">
              <div><Label>{t("pricing.service")}</Label><Input data-testid="row-service" value={rowEdit.service} onChange={(e) => setRowEdit({ ...rowEdit, service: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("pricing.category")}</Label><Input data-testid="row-category" value={rowEdit.category} onChange={(e) => setRowEdit({ ...rowEdit, category: e.target.value })} /></div>
                <div><Label>{t("pricing.unit")}</Label><Input data-testid="row-unit" value={rowEdit.unit} onChange={(e) => setRowEdit({ ...rowEdit, unit: e.target.value })} /></div>
                <div><Label>{t("pricing.clientPrice")}</Label><Input data-testid="row-price" type="number" step="0.01" value={rowEdit.price} onChange={(e) => setRowEdit({ ...rowEdit, price: e.target.value })} placeholder="bid" /></div>
              </div>
              <p className="font-mono text-[10px] text-zinc-500">Leave price blank for "by bid". This price is the billable amount and the contractor's allowable cap.</p>
              <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setRowEdit(null)}>{t("common.cancel")}</Btn><Btn data-testid="save-row-btn" onClick={saveRow}>{t("common.save")}</Btn></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaySheet() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/pricing/pay-rules").then((r) => setRules(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const svc = edit.service === "__default__" ? "__default__" : (edit.service || "").trim();
    if (svc !== "__default__" && !svc) { toast.error("Service required"); return; }
    try { await api.put("/pricing/pay-rules", { service: svc, pay_type: edit.pay_type, value: Number(edit.value) }); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => { if (!window.confirm(`Delete pay rule for "${r.service}"?`)) return; try { await api.delete("/pricing/pay-rules", { params: { service: r.service } }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const label = (r) => r.service === "__default__" ? t("pricing.defaultRule") : r.service;
  const desc = (r) => r.pay_type === "fixed" ? `$${Number(r.value).toFixed(2)}` : `${Number(r.value)}% ${t("pricing.percentOfClient").toLowerCase().includes("del") ? "del precio" : "of client price"}`;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] text-zinc-500 max-w-md">{t("pricing.payNote")}</p>
        <Btn data-testid="add-rule-btn" onClick={() => setEdit({ service: "", pay_type: "percent", value: 65 })}><Plus size={14} /> {t("pricing.addRule")}</Btn>
      </div>
      <Card className="p-0 overflow-hidden divide-y divide-zinc-800">
        {rules.map((r) => (
          <div key={r.service} data-testid={`rule-${r.service}`} className="flex items-center gap-3 px-4 py-3">
            <HandCoins size={16} className="text-[#FF5722] shrink-0" />
            <div className="flex-1 min-w-0"><div className="text-white text-sm truncate">{label(r)}</div></div>
            <div className="font-mono text-emerald-400 text-sm">{desc(r)}</div>
            <button data-testid={`edit-rule-${r.service}`} onClick={() => setEdit({ service: r.service, pay_type: r.pay_type, value: r.value })} className="text-zinc-500 hover:text-[#FF5722]"><Pencil size={14} /></button>
            {r.service !== "__default__" && <button data-testid={`del-rule-${r.service}`} onClick={() => del(r)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>}
          </div>
        ))}
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="rule-dialog"><DialogHeader><DialogTitle className="font-head uppercase">{t("pricing.paySheet")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              {edit.service !== "__default__" && <div><Label>{t("pricing.service")}</Label><Input data-testid="rule-service" value={edit.service} onChange={(e) => setEdit({ ...edit, service: e.target.value })} placeholder="e.g. Debris Removal (must match a sheet service)" /></div>}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("pricing.payType")}</Label><Select data-testid="rule-type" value={edit.pay_type} onChange={(e) => setEdit({ ...edit, pay_type: e.target.value })}><option value="percent">{t("pricing.percentOfClient")}</option><option value="fixed">{t("pricing.fixedAmount")}</option></Select></div>
                <div><Label>{edit.pay_type === "fixed" ? "$" : "%"}</Label><Input data-testid="rule-value" type="number" step="0.01" value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setEdit(null)}>{t("common.cancel")}</Btn><Btn data-testid="save-rule-btn" onClick={save}>{t("common.save")}</Btn></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
